import { useDialog } from "../components/DialogProvider";
import { ActionStatus } from "../components/ActionStatus";
/*
 * UploadModal — модальное окно загрузки файла.
 *
 * Состояния:
 *   - "pick": выбор файла (drag-drop или кнопка).
 *   - "uploading": идёт upload, показываем прогресс-бар и кнопку Отменить.
 *   - "done": загрузка завершена, показываем ссылку на запись.
 *   - "error": ошибка, кнопка "Закрыть" и описание.
 *
 * При закрытии модалки во время uploading автоматически вызывается cancel()
 * (предупреждение — иначе случайный клик мимо
 * убьёт час работы).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { hasGroup, useAuth } from "../auth/AuthContext";
import type { RecordingTag, RecordingVisibility } from "../types";
import { createTag, listTags } from "../api/tags";

import {
  MultipartUploader,
  UploadCancelled,
  type ResumeState,
} from "./MultipartUploader";
import {
  DEFAULT_SUMMARY_PROFILE,
  useSummaryProfiles,
  type SummaryProfile,
} from "../summaryProfiles";

/** Разрешённые расширения (соответствует backend uploadAllowedExts). */
const ALLOWED_EXTS = new Set([
  "mp4", "webm", "mov", "mkv", "avi", "m4v",
  "mp3", "m4a", "wav", "ogg", "opus", "flac",
]);

/** Расширения, которые играются в браузерном <video>/<audio>. */
const PLAYABLE_EXTS = new Set([
  "mp4", "webm", "m4v", "mp3", "m4a", "wav", "ogg", "opus", "flac",
]);

const MAX_FILE_SIZE = 5 * 1024 * 1024 * 1024;

export interface UploadModalProps {
  /** Если задан — стартуем в resume-режиме (юзер уже выбрал файл из resume-баннера). */
  resumeState?: ResumeState;
  /** Если задан вместе с resumeState — переиспользуем уже выбранный File. */
  resumeFile?: File;
  onClose: () => void;
  /** Вызывается после успешной загрузки. Список записей перезагружается. */
  onSuccess: (recordingId: string) => void;
}

type Phase = "pick" | "uploading" | "done" | "error";

export function UploadModal({
  resumeState,
  resumeFile,
  onClose,
  onSuccess,
}: UploadModalProps) {
  const { confirm } = useDialog();
  const [phase, setPhase] = useState<Phase>(resumeState ? "uploading" : "pick");
  const [file, setFile] = useState<File | null>(resumeFile ?? null);
  const [progress, setProgress] = useState(0); // 0..1
  const [uploadedBytes, setUploadedBytes] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultRecordingId, setResultRecordingId] = useState<string | null>(null);
  // Профиль AI-саммари для этой загрузки. Применяется только к свежему upload'у
  // (resume не пересоздаёт запись). Позже можно перегенерировать другим типом.
  const [profile, setProfile] = useState<SummaryProfile>(DEFAULT_SUMMARY_PROFILE);
  const [visibility, setVisibility] = useState<RecordingVisibility>("restricted");
  const [tags, setTags] = useState<RecordingTag[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagsLoading, setTagsLoading] = useState(true);
  const [tagsError, setTagsError] = useState("");
  const [creatingTag, setCreatingTag] = useState(false);
  const creatingTagRef = useRef(false);
  const startingRef = useRef(false);
  const { user } = useAuth();
  const canConfidential = hasGroup(user, "recordings-supermanager") || hasGroup(user, "recordings-admin");

  const uploaderRef = useRef<MultipartUploader | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const loadTags = useCallback(async () => {
    setTagsLoading(true);
    setTagsError("");
    try { setTags(await listTags()); }
    catch { setTagsError("Не удалось загрузить метки."); }
    finally { setTagsLoading(false); }
  }, []);
  useEffect(() => { void loadTags(); }, [loadTags]);

  // Если задан resumeState+resumeFile — стартуем upload автоматически при mount.
  useEffect(() => {
    if (resumeState && resumeFile && phase === "uploading" && !uploaderRef.current) {
      void runUpload(resumeFile, resumeState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Предотвращаем случайное закрытие во время uploading (Esc, click outside).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") void handleCloseRequest();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  async function handleCloseRequest() {
    if (phase === "uploading") {
      if (await confirm({ title: "Прервать загрузку?", description: "Прогресс будет потерян.", confirmLabel: "Прервать", cancelLabel: "Продолжить загрузку", danger: true })) {
        uploaderRef.current?.cancel();
        // Фактическое закрытие — после того как uploader среагирует на cancel
        // и сработает UploadCancelled в runUpload.
      }
      return;
    }
    onClose();
  }

  function validateFile(f: File): string | null {
    const ext = (f.name.split(".").pop() ?? "").toLowerCase();
    if (!ext || !ALLOWED_EXTS.has(ext)) {
      return `Расширение .${ext || "?"} не поддерживается. Допустимые: ${Array.from(ALLOWED_EXTS).join(", ")}.`;
    }
    if (f.size === 0) {
      return "Файл пустой.";
    }
    if (f.size > MAX_FILE_SIZE) {
      return `Файл слишком большой: ${formatBytes(f.size)}. Максимум 5 GB.`;
    }
    return null;
  }

  function onPickFile(f: File) {
    const err = validateFile(f);
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    setFile(f);
  }

  async function runUpload(f: File, resume?: ResumeState) {
    setPhase("uploading");
    setProgress(0);
    setUploadedBytes(0);
    setError(null);

    const callbacks = {
      onProgress: (fraction: number, bytes: number) => {
        setProgress(fraction);
        setUploadedBytes(bytes);
      },
    };

    let uploader: MultipartUploader;
    try {
      uploader = resume
        ? MultipartUploader.fromResume(resume, f, callbacks)
        : new MultipartUploader(f, callbacks, profile, visibility, tags.filter(t => selectedTags.includes(t.id)).map(t => ({id:t.id, version:t.version})));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setPhase("error");
      return;
    }
    uploaderRef.current = uploader;

    try {
      const recordingId = await uploader.start();
      setResultRecordingId(recordingId);
      setPhase("done");
      onSuccess(recordingId);
    } catch (e) {
      if (e instanceof UploadCancelled) {
        // Юзер отменил — просто закрываем.
        onClose();
        return;
      }
      // eslint-disable-next-line no-console
      console.error("[upload] failed:", e);
      setError(e instanceof Error ? e.message : String(e));
      setPhase("error");
    }
  }

  return (
    <div className="modal-backdrop" onClick={handleCloseRequest}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Загрузка файла</h2>
          <button
            type="button"
            className="link-button"
            onClick={handleCloseRequest}
            aria-label="Закрыть"
          >
            ✕
          </button>
        </div>

        {phase === "pick" && (
          <PickView
            file={file}
            dragOver={dragOver}
            onDragOver={setDragOver}
            onPickFile={onPickFile}
            error={error}
            fileInputRef={fileInputRef}
            profile={profile}
            onProfileChange={setProfile}
            visibility={visibility}
            onVisibilityChange={setVisibility}
            canConfidential={canConfidential}
            tags={tags}
            selectedTags={selectedTags}
            onSelectedTags={setSelectedTags}
            tagsLoading={tagsLoading}
            tagsError={tagsError}
            onRetryTags={() => void loadTags()}
            creatingTag={creatingTag}
            onCreateTag={async (name) => {
              if (creatingTagRef.current || !name.trim()) return false;
              creatingTagRef.current = true;
              try {
                setCreatingTag(true); setError(null);
                const t=await createTag(name.trim()); setTags(prev=>[...prev,t]);setSelectedTags(prev=>[...prev,t.id]);
                return true;
              } catch { setError("Не удалось создать метку"); return false; }
              finally { creatingTagRef.current = false; setCreatingTag(false); }
            }}
            onStart={() => void (async () => {
              if (startingRef.current || creatingTagRef.current) return;
              startingRef.current = true;
              try {
                const granting=tags.filter(t => selectedTags.includes(t.id) && t.grants_access);
                if (granting.length && !await confirm({ title: "Предоставить доступ?", description: `Доступ получат: ${granting.map(t=>`${t.name}: ${(t.grants ?? []).map(g=>g.principal).join(", ") || "нет получателей"}`).join("; ")}. Список получателей может измениться позднее.`, confirmLabel: "Загрузить" })) return;
                if(file) await runUpload(file);
              } finally { startingRef.current = false; }
            })()}
            onCancel={onClose}
          />
        )}

        {phase === "uploading" && file && (
          <UploadingView
            filename={file.name}
            fileSize={file.size}
            progress={progress}
            uploadedBytes={uploadedBytes}
            onCancel={handleCloseRequest}
          />
        )}

        {phase === "done" && resultRecordingId && (
          <DoneView
            filename={file?.name ?? ""}
            recordingId={resultRecordingId}
            onClose={onClose}
          />
        )}

        {phase === "error" && (
          <ErrorView error={error ?? "Неизвестная ошибка"} onClose={onClose} />
        )}
      </div>
    </div>
  );
}

function PickView({
  file,
  dragOver,
  onDragOver,
  onPickFile,
  error,
  fileInputRef,
  profile,
  onProfileChange,
  visibility,
  onVisibilityChange,
  canConfidential,
  tags,
  selectedTags,
  onSelectedTags,
  tagsLoading,
  tagsError,
  onRetryTags,
  creatingTag,
  onCreateTag,
  onStart,
  onCancel,
}: {
  file: File | null;
  dragOver: boolean;
  onDragOver: (v: boolean) => void;
  onPickFile: (f: File) => void;
  error: string | null;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  profile: SummaryProfile;
  onProfileChange: (p: SummaryProfile) => void;
  visibility: RecordingVisibility;
  onVisibilityChange: (v: RecordingVisibility) => void;
  canConfidential: boolean;
  tags: RecordingTag[];
  selectedTags: string[];
  onSelectedTags: (ids: string[]) => void;
  tagsLoading: boolean;
  tagsError: string;
  onRetryTags: () => void;
  creatingTag: boolean;
  onCreateTag: (name: string) => Promise<boolean>;
  onStart: () => void;
  onCancel: () => void;
}) {
  const { profiles } = useSummaryProfiles();
  const activeHint = profiles.find((p) => p.slug === profile)?.hint ?? "";
  return (
    <>
      <div
        className={`drop-zone${dragOver ? " drop-zone-active" : ""}`}
        onDragEnter={(e) => {
          e.preventDefault();
          onDragOver(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          onDragOver(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          onDragOver(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          onDragOver(false);
          const f = e.dataTransfer.files?.[0];
          if (f) onPickFile(f);
        }}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".mp4,.webm,.mov,.mkv,.avi,.m4v,.mp3,.m4a,.wav,.ogg,.opus,.flac"
          className="is-hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onPickFile(f);
            // Сбрасываем value, чтобы повторный выбор того же файла триггерил onChange.
            e.target.value = "";
          }}
        />
        {file ? (
          <>
            <p className="drop-zone-title">{file.name}</p>
            <p className="muted">{formatBytes(file.size)}</p>
            <p className="muted upload-hint">
              Кликните чтобы выбрать другой файл
            </p>
          </>
        ) : (
          <>
            <p className="drop-zone-title">Перетащите файл сюда</p>
            <p className="muted">или кликните чтобы выбрать</p>
            <p className="muted upload-formats">
              MP4, WebM, MOV, MKV, AVI, MP3, M4A, WAV, OGG, FLAC до 5 GB
            </p>
          </>
        )}
      </div>

      <div className="upload-profile">
        <label className="manager-label" htmlFor="upload-profile-select">
          Тип конспекта
        </label>
        <select
          id="upload-profile-select"
          className="filter-input input-full"
          value={profile}
          onChange={(e) => onProfileChange(e.target.value as SummaryProfile)}
        >
          {profiles.map((p) => (
            <option key={p.slug} value={p.slug}>
              {p.label}
            </option>
          ))}
        </select>
        <p className="muted upload-profile-hint">
          {activeHint} Можно сменить позже и перегенерировать.
        </p>
      </div>

      <div className="upload-profile">
        <label className="manager-label" htmlFor="upload-visibility">Доступ</label>
        <select id="upload-visibility" className="filter-input input-full" value={visibility} onChange={(e) => onVisibilityChange(e.target.value as RecordingVisibility)}>
          <option value="restricted">Ограниченный доступ</option>
          <option value="staff">Всем сотрудникам NAN</option>
          {canConfidential && <option value="confidential">Конфиденциальная</option>}
        </select>
        <p className="muted">{visibility === "staff" ? "Запись увидят сотрудники NAN с доступом к порталу. Клиентам доступ выдаётся отдельно." : visibility === "confidential" ? "Запись доступна владельцу и тем, кому вы предоставите доступ." : "Запись доступна владельцу, суперменеджерам и тем, кому вы предоставите доступ."}</p>
      </div>
      <UploadTagPicker tags={tags} selected={selectedTags} onSelected={onSelectedTags} loading={tagsLoading} error={tagsError} onRetry={onRetryTags} creating={creatingTag} onCreate={onCreateTag} />
      {error && <p className="error">{error}</p>}

      <div className="modal-actions">
        <button type="button" className="page-button" onClick={onCancel}>
          Отмена
        </button>
        <button
          type="button"
          className="page-button primary"
          disabled={!file || creatingTag}
          onClick={onStart}
        >
          Загрузить
        </button>
      </div>
    </>
  );
}

function UploadTagPicker({ tags, selected, onSelected, loading, error, onRetry, creating, onCreate }: {
  tags: RecordingTag[];
  selected: string[];
  onSelected: (ids: string[]) => void;
  loading: boolean;
  error: string;
  onRetry: () => void;
  creating: boolean;
  onCreate: (name: string) => Promise<boolean>;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const pickerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    const outside = (event: PointerEvent) => { if (!pickerRef.current?.contains(event.target as Node)) setOpen(false); };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") { event.stopPropagation(); setOpen(false); triggerRef.current?.focus(); }
    };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape);
    return () => { document.removeEventListener("pointerdown", outside); document.removeEventListener("keydown", escape); };
  }, [open]);
  const selectedTags = tags.filter(tag => selected.includes(tag.id));
  const search = query.trim().toLocaleLowerCase();
  const choices = tags.filter(tag => !tag.archived && !selected.includes(tag.id) && tag.name.toLocaleLowerCase().includes(search));
  const exact = tags.some(tag => tag.name.toLocaleLowerCase() === search);
  const select = (tag: RecordingTag) => {
    if (creating || selected.includes(tag.id)) return;
    onSelected([...selected, tag.id]);
    setOpen(false);
    setQuery("");
    triggerRef.current?.focus();
  };
  return <section className="upload-profile upload-tags" aria-label="Метки записи">
    <div className="recording-tags-heading">
      <div><span className="manager-label">Метки</span><p className="tag-caption">Помогают найти запись и управлять доступом</p></div>
      <div className="tag-picker" ref={pickerRef}>
        <button ref={triggerRef} className="tag-add-button" type="button" aria-expanded={open} aria-controls="upload-tag-picker" disabled={loading || !!error || creating} onClick={() => setOpen(!open)}><span aria-hidden="true">＋</span> Добавить метку</button>
        {open && <div className="tag-popover upload-tag-popover" id="upload-tag-picker" role="dialog" aria-label="Добавить метку">
          <input ref={inputRef} className="tag-search" type="search" placeholder="Найти или создать метку" aria-label="Поиск меток" value={query} disabled={creating} onChange={event => setQuery(event.target.value)} maxLength={150} />
          <div className="tag-options">
            {choices.map(tag => <button type="button" className="tag-option" key={tag.id} disabled={creating} onClick={() => select(tag)}><span className="tag-dot" style={tag.color ? { background: tag.color } : undefined} /><span className="tag-option-name">{tag.name}</span>{tag.grants_access && <span className="tag-option-access">С доступом</span>}</button>)}
            {search && !exact && <button type="button" className="tag-option tag-create" disabled={creating} onClick={() => void (async () => { if (await onCreate(query)) { setOpen(false); setQuery(""); triggerRef.current?.focus(); } })()}><span aria-hidden="true">＋</span><span>Создать «{query.trim()}»<small>Только для поиска, без выдачи доступа</small></span></button>}
            {!choices.length && (!search || exact) && <p className="tag-empty">{search ? "Метка уже добавлена или находится в архиве" : "Нет доступных меток. Введите название, чтобы создать."}</p>}
          </div>
        </div>}
      </div>
    </div>
    {selectedTags.length ? <div className="recording-tag-chips">{selectedTags.map(tag => <span className="recording-tag-chip" key={tag.id}><span className="tag-dot" style={tag.color ? { background: tag.color } : undefined} />{tag.name}{tag.grants_access && <span className="tag-chip-access" title="Метка выдаёт доступ">Доступ</span>}<button type="button" aria-label={`Убрать ${tag.name}`} disabled={creating} onClick={() => onSelected(selected.filter(id => id !== tag.id))}>×</button></span>)}</div> : <p className="tag-empty-current">Метки пока не добавлены</p>}
    {selectedTags.filter(tag => tag.grants_access).map(tag => <p className="tag-recipients" key={tag.id}><strong>{tag.name}:</strong> {(tag.grants ?? []).map(grant => grant.principal).join(", ") || "получателей пока нет"}</p>)}
    {(loading || creating) && <p className="action-status-row"><ActionStatus>{creating ? "Создаём метку…" : "Загружаем метки…"}</ActionStatus></p>}
    {error && <p className="error" role="alert">{error} <button type="button" className="link-button" onClick={onRetry}>Повторить</button></p>}
  </section>;
}

function UploadingView({
  filename,
  fileSize,
  progress,
  uploadedBytes,
  onCancel,
}: {
  filename: string;
  fileSize: number;
  progress: number;
  uploadedBytes: number;
  onCancel: () => void;
}) {
  const pct = Math.round(progress * 100);
  return (
    <>
      <div className="upload-progress-block">
        <p>
          <strong>{filename}</strong>
        </p>
        <div className="progress-track">
          <div
            className="progress-fill"
            style={{ width: `${pct}%` }}
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
        <p className="muted">
          {formatBytes(uploadedBytes)} / {formatBytes(fileSize)} ({pct}%)
        </p>
      </div>
      <div className="modal-actions">
        <button type="button" className="page-button" onClick={onCancel}>
          Прервать
        </button>
      </div>
    </>
  );
}

function DoneView({
  filename,
  recordingId,
  onClose,
}: {
  filename: string;
  recordingId: string;
  onClose: () => void;
}) {
  const ext = (filename.split(".").pop() ?? "").toLowerCase();
  const playable = PLAYABLE_EXTS.has(ext);
  return (
    <>
      <p>
        <strong>{filename}</strong> загружен.
      </p>
      <p className="muted upload-note">
        Транскрибация запущена. Результат появится в списке записей.
      </p>
      {!playable && (
        <p className="muted upload-note">
          ℹ️ Формат .{ext} не играется в браузере напрямую. После транскрибации
          можно будет скачать оригинал и посмотреть транскрипт.
        </p>
      )}
      <div className="modal-actions">
        <a href={`/r/${recordingId}`} className="page-button primary">
          Открыть запись
        </a>
        <button type="button" className="page-button" onClick={onClose}>
          Закрыть
        </button>
      </div>
    </>
  );
}

function ErrorView({ error, onClose }: { error: string; onClose: () => void }) {
  return (
    <>
      <p className="error">Загрузка не удалась</p>
      <pre className="upload-error">{error}</pre>
      <div className="modal-actions">
        <button type="button" className="page-button" onClick={onClose}>
          Закрыть
        </button>
      </div>
    </>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  const kb = n / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  return `${(mb / 1024).toFixed(2)} GB`;
}
